import {Fragment, useEffect, useState, useRef} from 'react'
import Chat from './Chat'
import Sidebar from './SidebarLeft'
import {useParams, useHistory} from 'react-router-dom'
import queryString from 'query-string'
import {useDispatch, useSelector} from 'react-redux'
import {
    getChatContactsFromServer,
    getUserProfileFromServer,
    getInactiveConversations
} from './store/actions'
import {BRANDS} from './store/actionTypes'
import InactiveConversationsCleanupModal from './InactiveConversationsCleanupModal'
import {getUserData} from '@src/utility/Utils'
import '@styles/base/pages/app-chat.scss'
import '@styles/base/pages/app-chat-list.scss'

const AppChat = () => {
    const {id} = useParams()
    const history = useHistory()
    const parsedQuery = queryString.parseUrl(window.location.href)?.query || {}
    const queryId = parsedQuery?.id || parsedQuery?.rd_id || parsedQuery?.conversationId
    const effectiveSelectedId = id || queryId

    // Normalize ?id= / ?rd_id= deep-links to standard /chatBrands/:id route
    useEffect(() => {
        if (!id && queryId) {
            history.replace(`/brands/apps/chat/chatBrands/${queryId}`)
        }
    }, [id, queryId, history])

    const dispatch = useDispatch()
    const chatBrandsStoreSlice = useSelector(state => state.chatBrands)

    const [user, setUser] = useState({})
    const [sidebar, setSidebar] = useState(false)
    const [userSidebarRight, setUserSidebarRight] = useState(false)
    const [userSidebarLeft, setUserSidebarLeft] = useState(false)
    const [showCleanupModal, setShowCleanupModal] = useState(false)
    const [cleanupData, setCleanupData] = useState({ count: 0, conversations: [] })

    const massMsgHandlerRef = useRef(null)

    const userData = getUserData()
    const currentProfile = chatBrandsStoreSlice?.brandUserProfile || userData
    const brandUserId = currentProfile?.parent > 0
        ? currentProfile.parent
        : (currentProfile?.parent_id > 0
            ? currentProfile.parent_id
            : (currentProfile?.chat_id || currentProfile?.id))
    const isBrand = !currentProfile?.role || currentProfile?.role === 'brand' || currentProfile?.user_type === 'brand'

    const handleSidebar = () => setSidebar(!sidebar)
    const handleUserSidebarLeft = () => setUserSidebarLeft(!userSidebarLeft)
    const handleUserSidebarRight = () => setUserSidebarRight(!userSidebarRight)

    const handleUser = obj => setUser(obj)

    const handleTabChange = (tab) => {
        dispatch({type: BRANDS.SET_ACTIVE_CHAT_TAB, tab})
        dispatch(getChatContactsFromServer(tab))
    }

    const handleMassMessageClick = () => {
        if (massMsgHandlerRef.current) {
            massMsgHandlerRef.current()
        }
    }

    // On mount: fetch all 3 tabs so lists are ready without needing a tab click
    useEffect(() => {
        dispatch(getUserProfileFromServer())
        dispatch({type: 'CLEAR_CHAT_CONTENT_ON_LOAD'})
        dispatch({type: 'CLEAR_CHAT_CONTACT_ON_LOAD'})
        dispatch(getChatContactsFromServer('inbox'))
        dispatch(getChatContactsFromServer('sent'))
        dispatch(getChatContactsFromServer('archive'))
    }, [dispatch])

    // Periodically check (once per month) for inactive conversations (> 6 months)
    useEffect(() => {
        if (!isBrand) return

        const effectiveId = brandUserId || currentProfile?.chat_id || currentProfile?.id
        const storageKey = effectiveId ? `rdlink_cleanup_prompt_brand_${effectiveId}` : null

        if (storageKey) {
            const storedThrottle = localStorage.getItem(storageKey)
            if (storedThrottle) {
                try {
                    const parsed = JSON.parse(storedThrottle)
                    const elapsed = Date.now() - (parsed?.timestamp || 0)
                    const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000
                    // Only throttle if user previously dismissed or completed the cleanup in the last 30 days
                    if (elapsed < ONE_MONTH_MS && (parsed?.status === 'dismissed' || parsed?.status === 'completed')) {
                        return
                    }
                } catch (e) {
                    // Ignore parse errors and check
                }
            }
        }

        dispatch(getInactiveConversations())
            .then(res => {
                if (res?.data?.status) {
                    const count = res.data.count || 0
                    const conversations = res.data.conversations || []
                    if (count > 2) {
                        setCleanupData({ count, conversations })
                        setShowCleanupModal(true)
                    }
                }
            })
            .catch(err => {
                console.error('Failed to load inactive conversations:', err)
            })
    }, [isBrand, brandUserId, currentProfile?.chat_id, currentProfile?.id, dispatch])

    const handleDismissCleanup = () => {
        const effectiveId = brandUserId || currentProfile?.chat_id || currentProfile?.id
        if (effectiveId) {
            localStorage.setItem(`rdlink_cleanup_prompt_brand_${effectiveId}`, JSON.stringify({
                timestamp: Date.now(),
                status: 'dismissed'
            }))
        }
        setShowCleanupModal(false)
    }

    const handleArchiveCompleted = (archivedCount) => {
        const effectiveId = brandUserId || currentProfile?.chat_id || currentProfile?.id
        if (effectiveId) {
            localStorage.setItem(`rdlink_cleanup_prompt_brand_${effectiveId}`, JSON.stringify({
                timestamp: Date.now(),
                status: 'completed',
                archivedCount
            }))
        }
        setShowCleanupModal(false)
    }

    const activeTab = chatBrandsStoreSlice?.activeTab || 'inbox'

    // Select the correct contacts list for the active tab
    const activeContacts = (() => {
        if (activeTab === 'sent')    return chatBrandsStoreSlice?.sentContacts
        if (activeTab === 'archive') return chatBrandsStoreSlice?.archiveContacts
        return chatBrandsStoreSlice?.inboxContacts
    })()

    return (
        <Fragment>
            <Sidebar
                store={chatBrandsStoreSlice}
                sidebar={sidebar}
                handleSidebar={handleSidebar}
                userSidebarLeft={userSidebarLeft}
                handleUserSidebarLeft={handleUserSidebarLeft}
                chatContacts={activeContacts}
                brandContacts={chatBrandsStoreSlice?.brandContacts}
                selectedUserID={effectiveSelectedId}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                onMassMessageClick={handleMassMessageClick}
            />
            <div className='content-right'>
                <div className='content-wrapper'>
                    <div className='content-body'>
                        <Chat
                            store={chatBrandsStoreSlice}
                            activeTab={activeTab}
                            handleUser={handleUser}
                            handleSidebar={handleSidebar}
                            userSidebarLeft={userSidebarLeft}
                            handleUserSidebarRight={handleUserSidebarRight}
                            selectedUserID={effectiveSelectedId}
                            currentUser={chatBrandsStoreSlice?.brandUserProfile}
                            selectedUserBrandChat={chatBrandsStoreSlice?.selectedUserBrandChat}
                            selectedContactDetail={chatBrandsStoreSlice?.selectedContactDetail}
                            onTabChange={handleTabChange}
                            setMassMessageTrigger={(fn) => { massMsgHandlerRef.current = fn }}
                        />
                    </div>
                </div>
            </div>
            <InactiveConversationsCleanupModal
                isOpen={showCleanupModal}
                toggle={() => setShowCleanupModal(!showCleanupModal)}
                inactiveData={cleanupData}
                onDismiss={handleDismissCleanup}
                onArchiveCompleted={handleArchiveCompleted}
            />
        </Fragment>
    )
}

export default AppChat